/**
 * v-drop-zone directive — Vue 3 drop-zone for calendar column cells.
 *
 * Usage: v-drop-zone (no binding value needed — reads data from tablesStore.drag)
 *
 * Applied to each `.column` <td> in Calendar_Week.vue.
 * Each cell must have:
 *   data-date="YYYY-MM-DD"
 *   data-time="HH:MM"
 *
 * Behaviour:
 *   - Handles BOTH drag sources:
 *     • HTML5 drag (sidebar services with draggable="true"):
 *       dragenter → highlight, dragleave → un-highlight, drop → fire logic
 *     • Pointer-based drag (existing events via v-interact):
 *       pointerenter → highlight, pointerleave → un-highlight, pointerup → fire logic
 *   - On drop, builds the event and calls:
 *       new event  → socketProxy NewCalendarEventDate
 *       move event → socketProxy UpdateCalendarEventDate
 */

import type { Directive } from 'vue'
import { useTablesStore } from '@/stores/tables'
import { useUserStore } from '@/stores/user'
import { useModalsStore } from '@/stores/modals'

// Simple uid generator (same as used elsewhere in the codebase)
function uid(): string {
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1)
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

function formatDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function buildEvent(dataobj: any, dropDate: string, dropTime: string, userId: string, activeLocation: string): any {
    const [hh, mm] = dropTime.split(':').map(Number)
    const datestart = new Date(dropDate)
    datestart.setHours(hh, mm, 0, 0)

    const durationMinutes = Number(dataobj.durationinminutes || dataobj.servicedata?.durationinminutes || 60)
    const dateend = new Date(datestart.getTime() + durationMinutes * 60_000)

    const isNew = dataobj.new === true

    let logs: any[] = []
    if (isNew) {
        logs = [{ date: formatDate(new Date()), kindof: 'datetimemanagement', note: 'Initial Creation', user: userId }]
    } else {
        try { logs = JSON.parse(dataobj.log || '[]') } catch { logs = [] }
        logs.push({ date: formatDate(new Date()), kindof: 'datetimemanagement', note: 'Update Date/Time by Dropping', user: userId })
    }

    const event: any = {
        companyid: dataobj.companyid || '',
        locationid: isNew ? activeLocation : (dataobj.locationid || activeLocation),
        roomid: dataobj.roomid || '',
        equipmentid: dataobj.equipmentid || '',
        datestartisotimezone: datestart.toISOString(),
        dateendisotimezone: dateend.toISOString(),
        datestart: formatDate(datestart),
        dateend: formatDate(dateend),
        log: JSON.stringify(logs),
        notes: dataobj.notes || '',
        options: dataobj.options || null,
        serviceid: isNew ? (dataobj.id || dataobj.serviceid || '') : (dataobj.serviceid || ''),
        // ISO-Z statt Wandzeit: der Server liest Wandzeit-Strings in seiner
        // Prozess-Zeitzone — Berliner Stempel wurden so als UTC gespeichert (+2 h).
        updated: new Date().toISOString(),
        updatedbyid: userId,
        archive: 0
    }

    if (isNew) {
        event.id = uid()
        event.created = new Date().toISOString()
        event.createdbyid = userId
    } else {
        event.id = dataobj.id
        event.created = dataobj.created || new Date().toISOString()
        event.createdbyid = dataobj.createdbyid || userId
        event.archive = dataobj.archive ?? 0
    }

    return event
}

function fireDrop(el: HTMLElement) {
    el.classList.remove('drop-activated')

    const ts = useTablesStore()
    // Read drag payload from store first. If vInteract.onPointerUp has already
    // reset the store (race: both listen to window pointerup), fall back to the
    // window.__vInteractLastDrag snapshot written at drag-start and cleared in a
    // queueMicrotask — always available to synchronous pointerup handlers.
    let drag = (ts as any).drag
    if (!drag?.draggedelement?.dataobj) {
        drag = (window as any).__vInteractLastDrag || drag
    }

    if (!drag?.draggedelement?.dataobj) {
        console.warn('[vDropZone] drop fired but no draggedelement found in store or snapshot', drag)
        return
    }

    const dataobj = drag.draggedelement.dataobj
    const dropDate = el.dataset.date
    const dropTime = el.dataset.time

    if (!dropDate || !dropTime) {
        console.warn('[vDropZone] missing date or time on drop target', el.dataset)
        return
    }

    const userStore = useUserStore()
    const userId = (userStore as any).Userdata?.id || ''
    const activeLocation = ts.config?.ActiveLocation || ''

    const event = buildEvent(dataobj, dropDate, dropTime, userId, activeLocation)
    const cmd = dataobj.new === true ? 'new' : 'update'

    console.log(`[vDropZone] ${cmd}`, event)

    ts.socketProxy({
        function: 'pullservice',
        cmd,
        table: 'calendar_events',
        data: event
    })

    // ── Optimistic local update ────────────────────────────────────────────────
    if (cmd === 'update' && event.id) {
        const tables = (ts as any).tables

        // 1. Primary store — tables.calendar_events
        if (Array.isArray(tables?.calendar_events)) {
            const idx = tables.calendar_events.findIndex((e: any) => e.id === event.id)
            if (idx !== -1) {
                tables.calendar_events.splice(idx, 1, {
                    ...tables.calendar_events[idx],
                    datestart: event.datestartisotimezone,
                    dateend: event.dateendisotimezone,
                    updated: event.updated,
                    log: event.log
                })
            }
        }

        // 2. Month temp cache — Calendar_events_month_cache (stores Date objects)
        const cache: any[] = (ts as any).temp?.Calendar_events_month_cache
        if (Array.isArray(cache)) {
            const [hh, mm] = dropTime.split(':').map(Number)
            const newStart = new Date(dropDate)
            newStart.setHours(hh, mm, 0, 0)
            const durationMs =
                (new Date(event.dateendisotimezone).getTime() - new Date(event.datestartisotimezone).getTime()) || 0
            const newEnd = new Date(newStart.getTime() + durationMs)

            const cIdx = cache.findIndex((e: any) => e.id === event.id)
            if (cIdx !== -1) {
                cache.splice(cIdx, 1, {
                    ...cache[cIdx],
                    datestart: newStart,
                    dateend: newEnd,
                    updated: new Date()
                })
            }
        }
    }

    if (cmd === 'new' && event.id) {
        // Open edit modal so user can configure room, resources, etc.
        // Do NOT optimistically insert — the server broadcast will add the event
        // to the store after the user confirms in the modal, avoiding duplication.
        if (drag.draggedelement.autoopenmodal) {
            try {
                const modalsStore = useModalsStore()
                modalsStore.openEditEventModal({ ...event, servicedata: dataobj, bookings: [] })
            } catch (e) {
                console.warn('[vDropZone] could not open edit modal', e)
            }
        }
    }

    // Reset drag state
    ;(ts as any).drag = { daysdropzone: false }
}


// ── Shared pointer handling (one window listener pair for ALL cells) ─────────
// Vorher registrierte JEDE Zelle zwei eigene window-Listener: die Woche hat
// ~480, die Monatsansicht ~2900 Zellen — jedes pointermove lief also durch
// tausende Handler mit je einem getBoundingClientRect (Forced Layout ×N).
// Auf einem iPad 8 stand der Kalender damit beim ersten Touch/Scroll still.
// Jetzt: EIN Listener-Paar, eine Registry der Zellen, und pro Move in der
// Regel genau ein Rect-Check (die zuletzt markierte Zelle zuerst).

const dropZoneCells = new Set<HTMLElement>()
let currentHighlighted: HTMLElement | null = null
let sharedListenersAttached = false

function isDragActive(): boolean {
    try {
        const ts = useTablesStore()
        const drag = (ts as any).drag
        return drag?.daysdropzone === true || drag?.drag === true
    } catch { return false }
}

function clearHighlight() {
    if (currentHighlighted) {
        currentHighlighted.classList.remove('drop-activated')
        currentHighlighted = null
    }
}

function cellContains(cell: HTMLElement, x: number, y: number, tolY = 0): boolean {
    const rect = cell.getBoundingClientRect()
    return x >= rect.left && x <= rect.right && y >= rect.top - tolY && y <= rect.bottom + tolY
}

/**
 * Erste Zelle (in Registrierungs- = DOM-Reihenfolge), die den Punkt enthält —
 * entspricht dem früheren "nur der erste Listener feuert"-Verhalten.
 * A 2px Y-tolerance catches the 1px border gap between adjacent time rows.
 */
function findCellAt(x: number, y: number, tolY = 0): HTMLElement | null {
    for (const cell of dropZoneCells) {
        if (cellContains(cell, x, y, tolY)) return cell
    }
    return null
}

function onSharedPointerMove(ev: PointerEvent) {
    if (!isDragActive()) {
        clearHighlight()
        return
    }
    // Schneller Pfad: Zeiger steht noch in der bereits markierten Zelle.
    if (currentHighlighted && cellContains(currentHighlighted, ev.clientX, ev.clientY)) return
    const cell = findCellAt(ev.clientX, ev.clientY)
    if (cell !== currentHighlighted) {
        clearHighlight()
        if (cell) {
            cell.classList.add('drop-activated')
            currentHighlighted = cell
        }
    }
}

function onSharedPointerUp(ev: PointerEvent) {
    // Always clear highlight on pointer release
    clearHighlight()
    const ts = useTablesStore()
    const drag = (ts as any).drag
    // Only handle pointer-based drags (v-interact), not HTML5 drags
    if (!drag?.drag) return
    const TOLERANCE_Y = 2
    const cell = findCellAt(ev.clientX, ev.clientY, TOLERANCE_Y)
    if (cell) {
        fireDrop(cell)
        ev.stopPropagation()
    }
}

function attachSharedListeners() {
    if (sharedListenersAttached) return
    window.addEventListener('pointermove', onSharedPointerMove)
    window.addEventListener('pointerup', onSharedPointerUp)
    sharedListenersAttached = true
}

function detachSharedListenersIfIdle() {
    if (!sharedListenersAttached || dropZoneCells.size > 0) return
    window.removeEventListener('pointermove', onSharedPointerMove)
    window.removeEventListener('pointerup', onSharedPointerUp)
    sharedListenersAttached = false
}

export const vDropZone: Directive = {
    mounted(el: HTMLElement) {

        // ── HTML5 drag (sidebar services) ────────────────────────────────────────

        function onDragEnter(ev: DragEvent) {
            ev.preventDefault()
            el.classList.add('drop-activated')
        }

        function onDragOver(ev: DragEvent) {
            // Required to allow drop
            ev.preventDefault()
            if (ev.dataTransfer) {
                ev.dataTransfer.dropEffect = 'copy'
            }
        }

        function onDragLeave(ev: DragEvent) {
            // Only remove highlight when leaving to an element outside this cell
            if (!el.contains(ev.relatedTarget as Node)) {
                el.classList.remove('drop-activated')
            }
        }

        function onDrop(ev: DragEvent) {
            ev.preventDefault()
            ev.stopPropagation()
            fireDrop(el)
        }

        el.addEventListener('dragenter', onDragEnter)
        el.addEventListener('dragover', onDragOver)
        el.addEventListener('dragleave', onDragLeave)
        el.addEventListener('drop', onDrop)

        // Pointer-based drag (existing events via v-interact): shared listeners
        dropZoneCells.add(el)
        attachSharedListeners()

            ; (el as any).__vDropZoneCleanup = () => {
                el.removeEventListener('dragenter', onDragEnter)
                el.removeEventListener('dragover', onDragOver)
                el.removeEventListener('dragleave', onDragLeave)
                el.removeEventListener('drop', onDrop)
                dropZoneCells.delete(el)
                if (currentHighlighted === el) clearHighlight()
                detachSharedListenersIfIdle()
                // Ensure highlight is cleared on unmount
                el.classList.remove('drop-activated')
            }

    },

    unmounted(el: HTMLElement) {
        ; (el as any).__vDropZoneCleanup?.()
    }
}
